// The one colour panel (AB#179, reworked by AB#281, shared with the prose editor by AB#287). In the
// slides editor the toolbar's Text colour, Highlight, Fill and Outline buttons open it as a popover
// (`ColourButton`), and the Format panel's Fill, Line and Text colour show it inline; the prose
// format toolbar's Text colour and Highlight open the same popover. The caller's palette as squares
// (the slides template's theme slots, or the prose palette), the per-use extras ("No fill",
// "No line", "No highlight", "Default colour"), the part's recently used custom colours, then a
// pipette that picks a colour anywhere on screen (the EyeDropper API; left out where the browser
// lacks it) and a "#" button opening a hex field (Enter applies, Esc cancels). A palette square is
// stored as its key (`accent1`, `green`), so it stays linked to its theme; anything else as `#RRGGBB`.
import { useEffect, useId, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { useLang } from '../../i18n'
import './colour-picker.css'

/** The EyeDropper API (Chromium): not in TypeScript's DOM library yet. */
interface EyeDropperLike {
  open: () => Promise<{ sRGBHex: string }>
}
type EyeDropperCtor = new () => EyeDropperLike

const eyeDropper = (): EyeDropperCtor | null =>
  typeof window !== 'undefined' && 'EyeDropper' in window ? ((window as unknown as { EyeDropper: EyeDropperCtor }).EyeDropper) : null

/** `#rgb`, `#rrggbb`, `rrggbb` or `rgb(r, g, b)` → `#RRGGBB`; null when it is none of them. */
export function normalizeHex(v: string): string | null {
  const s = v.trim()
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(s)
  if (rgb) return `#${rgb.slice(1, 4).map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('').toUpperCase()}`
  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s)?.[1]
  if (!hex) return null
  return `#${(hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex).toUpperCase()}`
}

/** A per-use swatch drawn after the palette: "No fill" (no `swatch`: the crossed square), a preset colour. */
export interface ColourExtra {
  key: string
  label: string
  swatch?: string
  disabled?: boolean
  onPick: () => void
}

export interface ColourPickerProps {
  /** The property it sets ("Fill", "Line", "Text colour"): the swatches' accessible names start with it. */
  label: string
  /** The palette's keys, one square each: the slides theme slots (`accent1`…), the prose colours (`green`…). */
  slots: readonly string[]
  /** A palette key → its CSS colour (the swatch's paint). */
  swatch: (slot: string) => string
  /** A palette key → its name for people (the swatch's accessible name and tooltip); the key itself by default. */
  slotName?: (slot: string) => string
  recent: string[]
  /** The current value when the selection has one (a slot or `#RRGGBB`): marks its swatch, seeds the hex field. */
  value?: string
  onPick: (colour: string) => void
  extras?: ColourExtra[]
  /** A press keeps the focus where it is (the caret in the text being edited), except in the hex field. */
  keepCaret?: boolean
  /** After any pick (a colour or an extra): the popover closes. */
  onDone?: () => void
  /** Focus the first swatch on mount (a popover opened from the keyboard). */
  autoFocus?: boolean
}

export function ColourPicker({ label, slots, swatch, slotName = (s) => s, recent, value, onPick, extras = [], keepCaret, onDone, autoFocus }: ColourPickerProps) {
  const { t } = useLang()
  const uid = useId()
  const current = value && value.startsWith('#') ? normalizeHex(value) : value ? normalizeHex(swatch(value)) : null
  const [hexOpen, setHexOpen] = useState(false)
  const [hex, setHex] = useState('')
  const [invalid, setInvalid] = useState(false)
  const hexBtnRef = useRef<HTMLButtonElement>(null)
  const firstRef = useRef<HTMLButtonElement>(null)
  const hold = keepCaret ? (e: MouseEvent) => e.preventDefault() : undefined
  const Dropper = eyeDropper()
  const [dropperFailed, setDropperFailed] = useState(false)

  useEffect(() => {
    if (autoFocus) firstRef.current?.focus()
  }, [autoFocus])

  const pick = (c: string): void => {
    onPick(c)
    onDone?.()
  }
  const openHex = (): void => {
    setHex(current ?? '')
    setInvalid(false)
    setHexOpen(true)
  }
  const closeHex = (): void => {
    setHexOpen(false)
    setInvalid(false)
    requestAnimationFrame(() => hexBtnRef.current?.focus())
  }
  const commitHex = (): void => {
    const v = normalizeHex(hex)
    if (!v) return setInvalid(true)
    setHexOpen(false)
    pick(v)
  }

  return (
    <div className="sl-colours">
      <div className="sl-swatches">
        {slots.map((slot, i) => (
          <button
            key={slot}
            ref={i === 0 ? firstRef : undefined}
            type="button"
            className="sl-swatch"
            aria-label={`${label} ${slotName(slot)}`}
            aria-pressed={value === slot}
            title={slotName(slot)}
            style={{ background: swatch(slot) }}
            onMouseDown={hold}
            onClick={() => pick(slot)}
          />
        ))}
        {extras.map((x) => (
          <button
            key={x.key}
            type="button"
            className={`sl-swatch ${x.swatch ? '' : 'sl-nofill'}`}
            aria-label={x.label}
            title={x.label}
            disabled={x.disabled}
            style={x.swatch ? { background: x.swatch } : undefined}
            onMouseDown={hold}
            onClick={() => {
              x.onPick()
              onDone?.()
            }}
          />
        ))}
      </div>
      {recent.length > 0 && (
        <div className="sl-swatches" role="group" aria-label={t('sl_recent_colours')}>
          {recent.map((c) => (
            <button
              key={c}
              type="button"
              className="sl-swatch"
              aria-label={`${label} ${c}`}
              aria-pressed={current === c && !!value?.startsWith('#')}
              title={c}
              style={{ background: c }}
              onMouseDown={hold}
              onClick={() => pick(c)}
            />
          ))}
        </div>
      )}
      <div className="sl-custom">
        {Dropper && (
          <button
            type="button"
            className="fmtbtn sl-dropper"
            aria-label={t('sl_eyedropper').replace('{what}', label)}
            title={t('sl_eyedropper').replace('{what}', label)}
            onMouseDown={hold}
            onClick={() => {
              setDropperFailed(false)
              new Dropper()
                .open()
                .then((r) => {
                  const v = normalizeHex(r.sRGBHex)
                  if (v) pick(v)
                })
                // Esc in the eyedropper is a rejection too: only a real failure is worth saying.
                .catch((e: unknown) => {
                  if (!(e instanceof DOMException && e.name === 'AbortError')) setDropperFailed(true)
                })
            }}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path d="M13.4 2.6a2 2 0 0 0-2.8 0L9 4.2 8.3 3.5 7.2 4.6l.7.7-5 5V13h2.7l5-5 .7.7 1.1-1.1-.7-.7 1.6-1.6a2 2 0 0 0 0-2.8zM4.2 12H4v-.2l4.8-4.8.2.2z" fill="currentColor" />
            </svg>
          </button>
        )}
        <button
          ref={hexBtnRef}
          type="button"
          className={`fmtbtn sl-hexbtn ${hexOpen ? 'on' : ''}`}
          aria-label={t('sl_hex_open').replace('{what}', label)}
          title={t('sl_hex_open').replace('{what}', label)}
          aria-expanded={hexOpen}
          onMouseDown={hold}
          onClick={() => (hexOpen ? closeHex() : openHex())}
        >
          <span aria-hidden="true">#</span>
        </button>
        {hexOpen && (
          <input
            type="text"
            className="sl-hex"
            aria-label={t('sl_hex_colour').replace('{what}', label)}
            aria-invalid={invalid}
            aria-describedby={invalid ? `${uid}-hexerr` : undefined}
            placeholder="#RRGGBB"
            spellCheck={false}
            autoFocus
            value={hex}
            onChange={(e) => {
              setHex(e.target.value)
              setInvalid(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitHex()
              } else if (e.key === 'Escape') {
                // The field only: the popover around it stays open.
                e.preventDefault()
                e.stopPropagation()
                closeHex()
              }
            }}
          />
        )}
      </div>
      {invalid && (
        <p className="sl-hint" id={`${uid}-hexerr`} role="alert">
          {t('sl_hex_invalid')}
        </p>
      )}
      {dropperFailed && (
        <p className="sl-hint" role="alert">
          {t('sl_eyedropper_failed')}
        </p>
      )}
    </div>
  )
}

/** What a colour button's bar shows: a colour (CSS), none set, or several values in the selection. */
export type ColourShown = { css: string; name: string } | 'none' | 'mixed'

export interface ColourButtonProps extends Omit<ColourPickerProps, 'label' | 'onDone' | 'autoFocus' | 'keepCaret'> {
  /** The button's accessible name ("Fill"); the panel's swatches are named after it too. */
  label: string
  /** The glyph above the swatch bar. */
  icon: ReactNode
  shown: ColourShown
  /** Inert, as the rest of its toolbar (the prose bar before a part is open). */
  disabled?: boolean
}

/**
 * A toolbar button that opens the colour panel as a popover (AB#281). Its swatch bar shows the
 * selection's current colour, "none" or "mixed"; the same is its accessible description. A press
 * keeps the caret in the text being edited; a keyboard opening moves into the panel; Esc closes it
 * and gives the focus back to the button.
 */
export function ColourButton({ label, icon, shown, disabled, ...picker }: ColourButtonProps) {
  const { t } = useLang()
  const uid = useId()
  const [open, setOpen] = useState<false | 'mouse' | 'keyboard'>(false)
  const ref = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: globalThis.MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])
  const done = (): void => {
    setOpen(false)
    // The panel is gone: the focus goes back to the button, unless the edited text took it.
    const a = document.activeElement
    if (!a || a === document.body || ref.current?.contains(a)) btnRef.current?.focus()
  }
  const current =
    shown === 'none' ? t('sl_colour_none') : shown === 'mixed' ? t('sl_colour_mixed') : shown.name
  return (
    <div className="sl-menu sl-colourmenu" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className="fmtbtn sl-colourbtn"
        aria-label={label}
        aria-describedby={`${uid}-current`}
        title={`${label} (${current})`}
        aria-haspopup="dialog"
        aria-expanded={!!open}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => setOpen(open ? false : e.detail === 0 ? 'keyboard' : 'mouse')}
      >
        <span className="sl-colourbtn-ico" aria-hidden="true">
          {icon}
        </span>
        <span
          className={`sl-colourbar ${shown === 'none' ? 'none' : shown === 'mixed' ? 'mixed' : ''}`}
          aria-hidden="true"
          style={typeof shown === 'object' ? { background: shown.css } : undefined}
        />
      </button>
      <span id={`${uid}-current`} hidden>
        {t('sl_colour_current').replace('{c}', current)}
      </span>
      {open && !disabled && (
        <div
          className="fmtpop sl-colour-pop"
          role="dialog"
          aria-label={label}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation()
              setOpen(false)
              btnRef.current?.focus()
            }
          }}
        >
          <ColourPicker label={label} {...picker} keepCaret onDone={done} autoFocus={open === 'keyboard'} />
        </div>
      )}
    </div>
  )
}
